import { createContext, useSyncExternalStore } from "react";

import { sortBy } from "es-toolkit";
import { isEqual } from "es-toolkit/compat";

import {
  COMMAND_GROUP_ORDER,
  type ActionCommand,
  type CollectionCommand,
  type ResolvedCommand,
} from "./types";

// Read side, used only by the palette. Commands are contributed declaratively
// by mounted <Command> components (Command.tsx) into the global singleton; this
// hook just subscribes to the flattened result.
export const useCommandRegistry = (): ResolvedCommand[] =>
  useSyncExternalStore(commandRegistry.subscribe, commandRegistry.getSnapshot);

type ScopeId = string;
type Listener = () => void;

// The fields the palette actually renders, searches on, or reads when recording
// recents. Diffing only these (not the whole command) is what lets a component
// re-register a fresh command object every render without churning
// subscribers: changes to run/getChildren
// don't appear here, so they never trigger a re-emit — they stay current via
// the per-scope latest binding instead (see register/wrap below).
const META_KEYS = [
  "id",
  "kind",
  "group",
  "title",
  "description",
  "icon",
  "keywords",
  "target",
  "badges",
  "disabled",
  "excludeFromRecents",
] as const;

const metaOf = (command: ResolvedCommand): Record<string, unknown> =>
  Object.fromEntries(
    META_KEYS.map((key) => [key, (command as Record<string, unknown>)[key]]),
  );

type Entry = {
  meta: Record<string, unknown>;
  latest: ResolvedCommand;
  wrapped: ResolvedCommand;
};

/**
 * Runtime registry of palette commands. Each mounted command component
 * registers under its own scope (see useRegisterCommand); the palette reads the
 * flattened list. The registry owns change detection: a component re-registers
 * its freshly built command on every render, and the registry only notifies
 * subscribers when display metadata actually changed (isEqual). It also keeps
 * the latest command per scope so run/getChildren closures never go stale.
 *
 * Implements the React useSyncExternalStore contract: getSnapshot returns a
 * stable reference until the next real change.
 */
export class CommandRegistry {
  private entries = new Map<ScopeId, Entry>();
  private listeners = new Set<Listener>();
  private snapshot: ResolvedCommand[] | null = null;
  private flushScheduled = false;

  // Called on every render of every command component. Doing the change
  // detection here (rather than via a deps array at the call site) is the whole
  // point of the design: callers just describe their command and never maintain
  // dependencies. Two things happen on each call:
  //   1. `latest` is updated unconditionally so run/getChildren always close
  //      over the freshest values, even between metadata changes.
  //   2. `wrapped` (what the snapshot exposes) is rebuilt — and subscribers
  //      notified — only when display metadata actually changed. That keeps the
  //      useSyncExternalStore snapshot referentially stable across the frequent
  //      no-op re-renders of the host, so the palette doesn't recompute.
  // The metaOf + isEqual cost is a tiny object and an ~8-field compare, paid
  // only per mounted command per render — negligible at the scales here.
  register = (scope: ScopeId, command: ResolvedCommand) => {
    const meta = metaOf(command);
    const existing = this.entries.get(scope);

    if (!existing) {
      if (import.meta.env.DEV) {
        for (const entry of this.entries.values()) {
          if (entry.latest.id === command.id) {
            throw new Error(
              `[commandPalette] duplicate command id "${command.id}" registered by multiple components`,
            );
          }
        }
      }
      this.entries.set(scope, {
        meta,
        latest: command,
        wrapped: this.wrap(scope, command),
      });
      this.scheduleEmit();
      return;
    }

    existing.latest = command;
    if (!isEqual(existing.meta, meta)) {
      existing.meta = meta;
      existing.wrapped = this.wrap(scope, command);
      this.scheduleEmit();
    }
  };

  unregister = (scope: ScopeId) => {
    if (this.entries.delete(scope)) this.scheduleEmit();
  };

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): ResolvedCommand[] => {
    if (this.snapshot === null) this.snapshot = this.compute();
    return this.snapshot;
  };

  /** Test helper: clears all registrations. */
  reset = () => {
    this.entries.clear();
    this.flushNow();
  };

  // Stable object: identity is fixed until metadata changes; closures read the
  // entry's latest command at call time so they never go stale.
  private wrap(scope: ScopeId, command: ResolvedCommand): ResolvedCommand {
    const live = () => this.entries.get(scope)?.latest ?? command;

    if (command.kind === "action")
      return { ...command, run: (ctx) => (live() as ActionCommand).run(ctx) };
    if (command.kind === "collection")
      return {
        ...command,
        getChildren: () => (live() as CollectionCommand).getChildren(),
      };
    return command; // navigation: no closures
  }

  // A page can mount dozens of command components at once (e.g. the service
  // page registers ~40). Coalescing their first-time registrations onto a
  // single microtask means the palette is notified once, not once per command.
  // The snapshot is invalidated synchronously so a read between now and the
  // flush is still correct.
  private scheduleEmit() {
    this.snapshot = null;
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    queueMicrotask(() => {
      this.flushScheduled = false;
      this.flushNow();
    });
  }

  private flushNow() {
    this.snapshot = null;
    this.listeners.forEach((listener) => listener());
  }

  private compute(): ResolvedCommand[] {
    // `entries` iterates in registration order (Map insertion order, which the
    // register/unregister paths preserve). sortBy is stable, so grouping by
    // the group's display rank keeps registration order within each group.
    return sortBy(
      [...this.entries.values()].map((entry) => entry.wrapped),
      [(command) => COMMAND_GROUP_ORDER.indexOf(command.group)],
    );
  }
}

export const commandRegistry = new CommandRegistry();
export const CommandRegistryContext =
  createContext<CommandRegistry>(commandRegistry);
